import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { NEUTRAL, RADIUS, VIOLET } from "../styles/brandTokens";
import { TIME_ENTRY_MAX_MINUTES, TIME_ENTRY_MIN_MINUTES } from "../types/integration";
import type { AgentTimeEntryMinutes } from "../types/integration";

/**
 * The Agent configure page's Time Entry step: minutes per Theo L1 category, each with optional L2 overrides.
 * An L2 left empty follows its L1; an L1 left empty follows Theo's default. Cortex logs the highest value
 * across a ticket's categories (src/common/utils/theo_ticket_taxonomy.py `resolve_time_entry_minutes`).
 */

/** The step's slice of the configure page's draft. Every value is what the admin typed; empty means inherit. */
export interface TimeEntryDraft {
  timeEntryMinutes: Record<string, string>;
  timeEntrySubMinutes: Record<string, Record<string, string>>;
  timeEntryUncategorized: string;
}

export interface TimeEntryDefaults {
  uncategorized: number;
  categories: Record<string, number>;
}

/** Digits only, at most three: the field never holds a fraction, a sign, or an exponent. */
export function minutesInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 3);
}

/** An empty field is fine (it means inherit); anything typed must be whole minutes in range. */
export function minutesProblem(value: string, label: string): string | null {
  if (!value) return null;
  const minutes = Number(value);
  return Number.isInteger(minutes) && minutes >= TIME_ENTRY_MIN_MINUTES && minutes <= TIME_ENTRY_MAX_MINUTES
    ? null
    : `Step 7: ${label} must be ${TIME_ENTRY_MIN_MINUTES} to ${TIME_ENTRY_MAX_MINUTES} minutes.`;
}

/** Every out-of-range value in the step, labelled the way its row is. */
export function timeEntryProblems(draft: TimeEntryDraft): string[] {
  return [
    ...Object.entries(draft.timeEntryMinutes).map(([l1, value]) => minutesProblem(value, l1)),
    ...Object.entries(draft.timeEntrySubMinutes).flatMap(([l1, byL2]) =>
      Object.entries(byL2).map(([l2, value]) => minutesProblem(value, `${l1} / ${l2}`))
    ),
    minutesProblem(draft.timeEntryUncategorized, "Unclassified tickets"),
  ].filter((problem): problem is string => Boolean(problem));
}

const asText = (minutes: unknown): string | null => (Number.isInteger(minutes) ? String(minutes) : null);

export function timeEntryDraftFromAgent(stored: AgentTimeEntryMinutes | null | undefined): TimeEntryDraft {
  const timeEntryMinutes: Record<string, string> = {};
  for (const [l1, minutes] of Object.entries(stored?.categories ?? {})) {
    const text = asText(minutes);
    if (text) timeEntryMinutes[l1] = text;
  }
  const timeEntrySubMinutes: Record<string, Record<string, string>> = {};
  for (const [l1, byL2] of Object.entries(stored?.subcategories ?? {})) {
    for (const [l2, minutes] of Object.entries(byL2 ?? {})) {
      const text = asText(minutes);
      if (text) timeEntrySubMinutes[l1] = { ...timeEntrySubMinutes[l1], [l2]: text };
    }
  }
  return { timeEntryMinutes, timeEntrySubMinutes, timeEntryUncategorized: asText(stored?.uncategorized) ?? "" };
}

/** What the save sends: only typed values. The server drops any equal to what they would inherit. */
export function timeEntryMinutesForSave(draft: TimeEntryDraft): AgentTimeEntryMinutes | null {
  const categories: Record<string, number> = {};
  for (const [l1, value] of Object.entries(draft.timeEntryMinutes)) {
    if (value) categories[l1] = Number(value);
  }
  const subcategories: Record<string, Record<string, number>> = {};
  for (const [l1, byL2] of Object.entries(draft.timeEntrySubMinutes)) {
    for (const [l2, value] of Object.entries(byL2)) {
      if (value) subcategories[l1] = { ...subcategories[l1], [l2]: Number(value) };
    }
  }
  const uncategorized = draft.timeEntryUncategorized ? Number(draft.timeEntryUncategorized) : null;
  if (uncategorized === null && Object.keys(categories).length === 0 && Object.keys(subcategories).length === 0) return null;
  return { uncategorized, categories, subcategories };
}

/** `values` with `key` set to `next`, or removed when `next` is empty, so clearing reads as unchanged again. */
function withValue(values: Record<string, string>, key: string, next: string): Record<string, string> {
  const rest = Object.fromEntries(Object.entries(values).filter(([existing]) => existing !== key));
  return next ? { ...rest, [key]: next } : rest;
}

/** `draft.timeEntrySubMinutes` with one L1's L2 values replaced, dropping the L1 when none are left. */
function withSubValues(
  all: Record<string, Record<string, string>>,
  l1: string,
  byL2: Record<string, string>
): Record<string, Record<string, string>> {
  const others = Object.fromEntries(Object.entries(all).filter(([existing]) => existing !== l1));
  return Object.keys(byL2).length ? { ...others, [l1]: byL2 } : others;
}

const helpSx = { fontSize: 12.5, lineHeight: 1.5, color: NEUTRAL.textSecondary } as const;
const fieldSx = {
  width: 120,
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.control}px`,
    backgroundColor: NEUTRAL.surface,
    fontSize: 14,
    "& fieldset": { borderColor: NEUTRAL.border },
    "&:hover fieldset": { borderColor: NEUTRAL.borderStrong },
  },
} as const;

interface MinutesRowProps {
  id: string;
  label: string;
  hint: string;
  value: string;
  /** What an empty field logs, shown as the placeholder. */
  inheritedMinutes: number | undefined;
  /** Names the inherited value once the field overrides it, e.g. "Default" or the L1. */
  inheritedLabel: string;
  nested?: boolean;
  toggle?: React.ReactNode;
  onChange: (next: string) => void;
}

function MinutesRow({ id, label, hint, value, inheritedMinutes, inheritedLabel, nested = false, toggle, onChange }: MinutesRowProps) {
  const problem = minutesProblem(value, label);
  const overridden = Boolean(value) && Number(value) !== inheritedMinutes;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: nested ? 0.75 : 1,
        pl: nested ? 3 : 0,
        borderTop: `1px solid ${NEUTRAL.border}`,
        ...(nested ? { backgroundColor: NEUTRAL.subtle } : {}),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="label"
          htmlFor={id}
          sx={{ fontSize: nested ? 13.5 : 14, fontWeight: nested ? 500 : 600, color: NEUTRAL.text, display: "block" }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: problem ? "error.main" : NEUTRAL.textSecondary }}>
          {problem
            ? `Use ${TIME_ENTRY_MIN_MINUTES} to ${TIME_ENTRY_MAX_MINUTES} minutes.`
            : overridden && inheritedMinutes !== undefined
              ? `${inheritedLabel} ${inheritedMinutes} min`
              : hint}
        </Typography>
        {toggle}
      </Box>
      {overridden ? (
        <Button
          size="small"
          onClick={() => onChange("")}
          aria-label={`Reset ${label} to ${nested ? "its category" : "the default"}`}
          sx={{ textTransform: "none", fontSize: 12.5, color: VIOLET.theoText, minWidth: 0 }}
        >
          Reset
        </Button>
      ) : null}
      <TextField
        id={id}
        size="small"
        value={value}
        error={Boolean(problem)}
        onChange={(event) => onChange(minutesInput(event.target.value))}
        placeholder={inheritedMinutes !== undefined ? String(inheritedMinutes) : ""}
        inputProps={{ inputMode: "numeric", "aria-label": `${label} minutes` }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: 13, color: NEUTRAL.textMuted }}>min</Typography>
            </InputAdornment>
          ),
        }}
        sx={fieldSx}
      />
    </Box>
  );
}

interface TimeEntryMinutesListProps {
  /** L1 categories in taxonomy order, each with its L2s. */
  categories: { l1: string; l2s: string[] }[];
  defaults: TimeEntryDefaults | null;
  draft: TimeEntryDraft;
  onChange: (patch: Partial<TimeEntryDraft>) => void;
}

export function TimeEntryMinutesList({ categories, defaults, draft, onChange }: TimeEntryMinutesListProps) {
  // Categories with a saved L2 override start open, so a change is never hidden.
  const [open, setOpen] = useState<Set<string>>(() => new Set(Object.keys(draft.timeEntrySubMinutes)));
  const toggleOpen = (l1: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(l1)) next.delete(l1);
      else next.add(l1);
      return next;
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {categories.map(({ l1, l2s }) => {
        const l1Default = defaults ? (defaults.categories[l1] ?? defaults.uncategorized) : undefined;
        const l1Typed = draft.timeEntryMinutes[l1] ?? "";
        // What an empty L2 field logs: the typed L1 value when it is valid, else the L1 default.
        const l1Effective = l1Typed && !minutesProblem(l1Typed, l1) ? Number(l1Typed) : l1Default;
        const subValues = draft.timeEntrySubMinutes[l1] ?? {};
        const subCount = Object.values(subValues).filter(Boolean).length;
        const isOpen = open.has(l1);
        const panelId = `time-entry-l2-${l1}`;
        return (
          <Box key={l1}>
            <MinutesRow
              id={`time-entry-${l1}`}
              label={l1}
              hint="Using the default"
              value={l1Typed}
              inheritedMinutes={l1Default}
              inheritedLabel="Default"
              onChange={(next) => onChange({ timeEntryMinutes: withValue(draft.timeEntryMinutes, l1, next) })}
              toggle={
                l2s.length > 0 ? (
                  <Button
                    size="small"
                    onClick={() => toggleOpen(l1)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{ transition: "transform 150ms ease", transform: isOpen ? "rotate(180deg)" : "none" }}
                      />
                    }
                    sx={{ textTransform: "none", fontSize: 12, color: VIOLET.theoText, px: 0, minWidth: 0, mt: 0.25 }}
                  >
                    {subCount > 0 ? `${l2s.length} subcategories · ${subCount} set` : `${l2s.length} subcategories`}
                  </Button>
                ) : null
              }
            />
            {isOpen ? (
              <Box id={panelId} role="group" aria-label={`${l1} subcategories`}>
                {l2s.map((l2) => (
                  <MinutesRow
                    key={l2}
                    id={`time-entry-${l1}-${l2}`}
                    label={l2}
                    hint={`Using ${l1}`}
                    value={subValues[l2] ?? ""}
                    inheritedMinutes={l1Effective}
                    inheritedLabel={l1}
                    nested
                    onChange={(next) =>
                      onChange({
                        timeEntrySubMinutes: withSubValues(draft.timeEntrySubMinutes, l1, withValue(subValues, l2, next)),
                      })
                    }
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        );
      })}
      <Box sx={{ mt: 0.5 }}>
        <MinutesRow
          id="time-entry-__uncategorized__"
          label="Unclassified tickets"
          hint="Tickets Theo could not place in a category."
          value={draft.timeEntryUncategorized}
          inheritedMinutes={defaults?.uncategorized}
          inheritedLabel="Default"
          onChange={(next) => onChange({ timeEntryUncategorized: next })}
        />
      </Box>
      <Typography sx={{ ...helpSx, mt: 1 }}>
        A ticket in more than one category logs the highest of their minutes.
      </Typography>
    </Box>
  );
}
